// client/src/components/pages/AmendmentsAllocateTransactions/FindReplaceBar.tsx
// (converted from .js, ST-00221). Find & Replace in Table Edit Mode: queues pending edits on
// the current page; nothing is saved until "Save All Changes".
import { useState, useMemo, useCallback, type KeyboardEvent } from 'react';
import InlineEditableCell from './InlineEditableCell';
import ThemedSelect from '../../common/ThemedSelect';
import { cellMatches, replaceInCell, type MatchType } from './findReplace';
import { isValidWeight, isWeightField, weightMessage } from './amendmentFields';
import type { AmendmentTicketRow } from '../../../types/clientPages/amendments';

/** One queued replacement: the ticket and the cell's new text. */
export interface FindReplacement {
  ticketUID: string;
  newValue: string;
}

/** The slice of a table column the bar reads. */
export interface FindReplaceColumn {
  Header: string;
  accessor?: string;
  id?: string;
}

export interface FindReplaceBarProps {
  data: AmendmentTicketRow[];
  editableColumns: FindReplaceColumn[];
  onApplyReplace: (columnAccessor: string, replacements: FindReplacement[]) => void;
  onClose: () => void;
}

const MATCH_TYPES: ReadonlySet<string> = new Set(['contains', 'exact', 'startsWith', 'endsWith']);

const FindReplaceBar = ({
  data,
  editableColumns,
  onApplyReplace,
  onClose,
}: FindReplaceBarProps) => {
  const [selectedColumn, setSelectedColumn] = useState('');
  const [findValue, setFindValue] = useState('');
  const [replaceValue, setReplaceValue] = useState('');
  const [matchType, setMatchType] = useState<MatchType>('contains');
  const [error, setError] = useState<string | null>(null);

  const columns = useMemo(
    () =>
      (editableColumns || []).filter((col) =>
        InlineEditableCell.isEditable(col.accessor || col.id)
      ),
    [editableColumns]
  );

  const matches = useMemo(() => {
    if (!selectedColumn || !findValue.trim() || !data) return [];
    return data.filter((row) =>
      cellMatches(String(row[selectedColumn] ?? ''), findValue, matchType)
    );
  }, [data, selectedColumn, findValue, matchType]);

  const weightColumn = isWeightField(selectedColumn);

  const handleApply = useCallback(() => {
    if (matches.length === 0 || !selectedColumn) return;
    // A weight is a number: substring edits of it make nonsense ("51.35" with "51.3" → "51.4"
    // would be "51.45"), so a matching weight cell is set to the replace value, which must be
    // a number (a blank weight would be saved as NULL and show the source weight, AMD-21).
    if (weightColumn && !isValidWeight(replaceValue)) {
      setError(`${weightMessage(selectedColumn)} Nothing replaced.`);
      return;
    }
    setError(null);
    onApplyReplace(
      selectedColumn,
      matches.map((row) => ({
        ticketUID: row.TicketUID,
        newValue: weightColumn
          ? replaceValue.trim()
          : replaceInCell(String(row[selectedColumn] ?? ''), findValue, replaceValue, matchType),
      }))
    );
  }, [matches, selectedColumn, weightColumn, replaceValue, findValue, matchType, onApplyReplace]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && matches.length > 0) {
        handleApply();
      } else if (e.key === 'Escape') {
        onClose();
      }
    },
    [handleApply, matches.length, onClose]
  );

  return (
    <div className="bg-surface border-b border-slate-200 px-4 py-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <svg
            className="w-4 h-4 text-slate-400 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
            />
          </svg>
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Find & Replace
          </span>
        </div>

        <div className="min-w-[140px]">
          <ThemedSelect
            aria-label="Column"
            value={selectedColumn}
            onChange={(e) => {
              setSelectedColumn(String(e.target.value));
              setError(null);
            }}
            placeholder="Select column..."
            className="block w-full pl-2 pr-7 py-1 text-xs border border-slate-300 bg-surface rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select column...</option>
            {columns.map((col) => (
              <option key={col.accessor || col.id} value={col.accessor || col.id}>
                {col.Header}
              </option>
            ))}
          </ThemedSelect>
        </div>

        <div className="min-w-[110px]">
          <ThemedSelect
            aria-label="Match type"
            value={matchType}
            onChange={(e) => {
              const next = String(e.target.value);
              if (MATCH_TYPES.has(next)) setMatchType(next as MatchType);
            }}
            className="block w-full pl-2 pr-7 py-1 text-xs border border-slate-300 bg-surface rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="contains">Contains</option>
            <option value="exact">Exact match</option>
            <option value="startsWith">Starts with</option>
            <option value="endsWith">Ends with</option>
          </ThemedSelect>
        </div>

        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={findValue}
            onChange={(e) => {
              setFindValue(e.target.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Find..."
            className="text-xs border border-slate-300 rounded-md px-2.5 py-1.5 w-40 focus:ring-1 focus:ring-primary-400 focus:border-primary-400 outline-none"
            autoFocus
          />
          <svg
            className="w-3.5 h-3.5 text-slate-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
          <input
            type="text"
            value={replaceValue}
            onChange={(e) => {
              setReplaceValue(e.target.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Replace with..."
            className="text-xs border border-slate-300 rounded-md px-2.5 py-1.5 w-40 focus:ring-1 focus:ring-primary-400 focus:border-primary-400 outline-none"
          />
        </div>

        {selectedColumn && findValue.trim() && (
          <span
            className={`text-xs font-medium tabular-nums ${matches.length > 0 ? 'text-amber-600' : 'text-slate-400'}`}
          >
            {matches.length} match{matches.length !== 1 ? 'es' : ''} on this page
          </span>
        )}

        <button
          onClick={handleApply}
          disabled={matches.length === 0}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Replace All ({matches.length})
        </button>

        <button
          onClick={onClose}
          className="ml-auto inline-flex items-center p-1 text-slate-400 hover:text-slate-600 rounded hover:bg-slate-100 transition-colors"
          title="Close Find & Replace"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {weightColumn && (
        <p className="mt-1.5 text-[11px] text-slate-500">
          Weights: a matching cell is set to the replace value as a whole.
        </p>
      )}
      {error && (
        <p role="alert" className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export default FindReplaceBar;
